<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router'
import {computed, onMounted, reactive, ref, watch, provide, nextTick} from 'vue'
import { RouteNames } from '@/router'
import { useAdminDesignerStore } from '@/stores/adminDesignerStore'
import { useDesignerStore } from '@/stores/designerStore'
import {
  getCustomImage,
  getPublicProductBySlug,
  createCartItem,
  updateCartItem
} from '@/api/frontend'
import Wizard from '@/components/wizard/Wizard.vue'
import WizardStep from '@/components/wizard/WizardStep.vue'
import DesignerCanvas from '@/components/designer/canvas/DesignerCanvas.vue'
import ShapesWidget from '@/components/designer/widget/ShapesWidget.vue'
import ShapesWidgetMobile from '@/components/designer/widget/ShapesWidgetMobile.vue'
import LabelPreview from '@/components/designer/LabelPreview.vue'
import ReadonlyInspector from '@/components/designer/widget/ReadonlyInspector.vue'
import { makeDesignerSnapshot } from '@/utils/snapshot'
import { useCanvasUnits } from '@/composables/useCanvasUnits'
import type { WizardContext } from '@/types/wizard'
import { DesignerMode } from '@/types'
import MaterialAndQuantity from '@/components/checkout/MaterialAndQuantity.vue'
import { useCartStore } from '@/stores/cart'
import {usePricingStore} from "@/stores/pricing.ts";

/* -------------------------------------------------------
 *  ROUTER + STORES
 * ----------------------------------------------------- */
const router = useRouter()
const route = useRoute()
const adminStore = useAdminDesignerStore()
const designerStore = useDesignerStore()
const cartStore = useCartStore()
adminStore.bootstrapFromWindow()

/* -------------------------------------------------------
 *  PARAMS
 * ----------------------------------------------------- */
const productSlug = ref<string | null>(null)
const productId = ref<number | null>(null)

const cartItemId = computed(() =>
  route.params.cartItemId ? Number(route.params.cartItemId) : null
)

const mode = computed<DesignerMode | null>(() => {
  if (route.name === RouteNames.CustomImageDesigner) return DesignerMode.CustomImage
  if (route.name === RouteNames.PredefinedLabelDesigner) return DesignerMode.PredefinedLabel
  if (route.name === RouteNames.CartItemDetail) return DesignerMode.CartItemDetail
  return null
})

/* -------------------------------------------------------
 *  WIZARD STEPS
 * ----------------------------------------------------- */
enum WizardSteps {
  Designer = 'Designer',
  MaterialAndQuantity = 'MaterialAndQuantity',
}
const activeStep = ref<WizardSteps>(WizardSteps.Designer)

/* -------------------------------------------------------
 *  SHARED ITEM (provide/inject)
 * ----------------------------------------------------- */
const item = reactive({
  material: 'paper',
  quantity: 1,
  price: null as number | null
})

provide('item', item)

/* -------------------------------------------------------
 *  SAVE (update/create cart item)
 * ----------------------------------------------------- */
async function saveMaterialAndQuantity() {
  // Update cart item
  if (mode.value === DesignerMode.CartItemDetail && cartItemId.value) {
    return await updateCartItem(cartItemId.value, {
      designerConfig: {
        ...designerStore.designerConfig,
        width_mm: designerStore.canvasSizeInMm.width,
        height_mm: designerStore.canvasSizeInMm.height
      },
      material: item.material as 'paper' | 'plastic',
      quantity: item.quantity
    })
  }

  // Create new cart item
  if (!productId.value) return null

  return await createCartItem({
    product_id: productId.value,
    designerConfig: {
      ...designerStore.designerConfig,
      width_mm: designerStore.canvasSizeInMm.width,
      height_mm: designerStore.canvasSizeInMm.height
    },
    material: item.material as 'paper' | 'plastic',
    quantity: item.quantity
  })
}

provide('saveMaterialAndQuantity', saveMaterialAndQuantity)
const pricingStore = usePricingStore()
const isPreviewMode = computed(() => adminStore.embedded && (adminStore.readonly || !!adminStore.previewMeta))
const previewMeta = computed(() => adminStore.previewMeta || {})
const previewTitle = computed(() => {
  return (previewMeta.value?.productName as string) || 'Design preview'
})
const previewOrderProductId = computed(() => previewMeta.value?.orderProductId as number | null)
const previewMaterial = computed(() => previewMeta.value?.material as string | null)
const previewQuantity = computed(() => previewMeta.value?.quantity as number | null)

function formatMaterial(value: string | null) {
  if (!value) return ''
  return value.charAt(0).toUpperCase() + value.slice(1)
}

/* -------------------------------------------------------
 *  ON MOUNT
 * ----------------------------------------------------- */
onMounted(async () => {
  cartStore.fetchCart()
  pricingStore.fetch()

  //await designerStore.whenCanvasReady()



  /* ------------------ CART ITEM DETAIL MODE ------------------ */
  if (mode.value === DesignerMode.CartItemDetail && cartItemId.value) {
    await cartStore.getCartItemDetail(cartItemId.value)

    const dto = cartStore.cartItemDetail.item
    if (dto) {
      designerStore.applyConfig(dto.designer_config)
      await nextTick()
      designerStore.bumpScale()
      item.material = dto.material ?? 'paper'
      item.quantity = dto.sticker_quantity ?? 1
    }
    return
  }

  /* ------------------ EMBEDDED MODE ------------------ */
  if (adminStore.embedded) {
    if (adminStore.config) designerStore.applyConfig(adminStore.config)
    if (!adminStore.readonly) await adminStore.fetchProduct()
    return
  }

  /* ------------------ CUSTOM IMAGE MODE ------------------ */
  if (mode.value === DesignerMode.CustomImage) {
    const imageId = Number(route.params.imageId)

    // 1. Fetch Image Info
    const imgData = await getCustomImage(imageId)

    // 2. Fetch Base Product "custom-label"
    // Adjust if the slug is different. User mentioned "custom-label".
    const dto = await getPublicProductBySlug('custom-label')
    productId.value = dto.id

    // 3. Apply Config
    // Merge product defaults with our custom image
    const baseConfig = dto.designer_config || {}
    
    // We can inject the image into the config.
    // Assuming the store/canvas can handle 'labelImage' or specific objects.
    // Since I don't see the store implementation fully, I'll pass it to designerStore if it has a method, 
    // or just set it in the config we apply.
    
    // If we want the image to be the background or a main element:
    // We might need to add it as an object if the Designer supports object lists.
    // Or set it as `labelImage` property if that's how it works.
    
    // Let's assume we update the config to include this image.
    // We'll preserve other defaults from the product.
    // AND store the customImageId so we can save it later (for cleanup protection).
    
    const newConfig = {
       ...baseConfig,
       customImageId: imgData.id, // For cleanup job protection
       labelImage: {
          imageSrc: imgData.url,
          position: { x: 0, y: 0 },
          dimensions: { width: 100, height: 100 },
          fit: 'contain' as const
       }
    }
    
    designerStore.applyConfig(newConfig)
    await nextTick()
    designerStore.bumpScale()
    return
  }

  /* ------------------ DEFAULT STICKER PRODUCT ------------------ */
  const slug = String(route.params.slug || '')
  if (!slug) return

  productSlug.value = slug
  const dto = await getPublicProductBySlug(slug)

  productId.value = dto.id
  designerStore.applyConfig(dto?.designer_config || {})

  await nextTick()
  designerStore.bumpScale()
})

watch(
  () => route.name,
  (newName, oldName) => {
    if (
      oldName === RouteNames.PredefinedLabelDesigner ||
      oldName === RouteNames.CustomImageDesigner
    ) {
      // Ak som prešiel z Designer na CartItemDetail, prepneme krok
      if (newName === RouteNames.CartItemDetail) {
        activeStep.value = WizardSteps.MaterialAndQuantity
      }
    }
  }
)

async function handleContinue(wizard: WizardContext) {
  // Upravujem existujúcu položku
  if (mode.value === DesignerMode.CartItemDetail && cartItemId.value) {
    await saveMaterialAndQuantity()
    wizard.goToNextStep()
    return
  }

  // Vytváram NOVÚ položku
  const dto = await saveMaterialAndQuantity()
  if (dto?.id) {
    // počkáme na navigáciu a až potom prepneme krok
    await router.push({
      name: RouteNames.CartItemDetail,
      params: { cartItemId: dto.id }
    })
    // Teraz sme už na CartItemDetail route → manuálne prepni krok 2
    activeStep.value = WizardSteps.MaterialAndQuantity
  }
}
</script>

<template>
  <main style="background: #faf4e7; padding: 100px 0; min-height: 100vh">
    <!--<div class="svg-wrapper">
      <LabelPreview :config="designerStore.designerConfig" />
    </div>-->
    <div class="container mx-auto">
      <Wizard v-model="activeStep" :hide-breadcrumbs="isPreviewMode">
        <template v-if="isPreviewMode" #title>
          <div class="text-center">
            <h2 class="text-h2 font-bold text-black">
              {{ previewTitle }}
            </h2>
            <div v-if="previewOrderProductId" class="text-sm text-gray-500 mt-1">
              Order item #{{ previewOrderProductId }}
            </div>
          </div>
        </template>
        <template v-if="isPreviewMode" #afterTitle>
          <div
            class="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-600 mt-3"
          >
            <div v-if="previewMaterial">
              Material: <span class="font-medium text-gray-800">{{ formatMaterial(previewMaterial) }}</span>
            </div>
            <div v-if="previewQuantity">
              Quantity: <span class="font-medium text-gray-800">{{ previewQuantity }}</span>
            </div>
          </div>
        </template>
        <!-- Optional header slots -->
        <template #beforeTitle v-if="false">
          <p class="text-sm text-gray-500 text-center">Text</p>
        </template>

        <template #afterTitle v-if="false">
          <p class="text-center text-xs text-gray-400">Popis kroku...</p>
        </template>

        <!-- Wizard content -->
        <template #default="{ wizard }">
          <!-- DESIGNER STEP -->
          <WizardStep :name="WizardSteps.Designer">
            <div class="grid grid-cols-12 gap-4">
              <div class="hidden md:block md:col-span-3">
                <ReadonlyInspector v-if="isPreviewMode" />
                <ShapesWidget
                  v-else
                  @continue-clicked="handleContinue(wizard)"
                  :mode="mode"
                />
              </div>

              <div class="col-span-12 md:col-span-9">
                <DesignerCanvas />
                <ReadonlyInspector v-if="isPreviewMode" class="mt-4 md:hidden" />
              </div>
            </div>

            <!-- Mobile controls - inside wizard context -->
            <ShapesWidgetMobile
              v-if="!isPreviewMode"
              class="block md:hidden"
              @continue-clicked="handleContinue(wizard)"
            />
          </WizardStep>

          <!-- MATERIAL & QUANTITY -->
          <WizardStep :name="WizardSteps.MaterialAndQuantity">
            <MaterialAndQuantity />
          </WizardStep>
        </template>
      </Wizard>
    </div>
  </main>
</template>

<style lang="scss">
.snapshotting .rectangle-wrapper {
  display: none !important;
}
.snapshotting .resize-handle {
  display: none !important;
}
.snapshotting .text-layer {
  border: none !important;
}
.snapshotting .draggable-container {
  border: none !important;
}
.snapshotting .label-image {
  display: block !important;
  opacity: 1 !important;
  visibility: visible !important;
}
</style>

<style scoped lang="scss">
.svg-wrapper {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.svg-wrapper svg {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
</style>
